import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { LoginPage } from '@/pages/Login';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { ResetPasswordPage } from '@/pages/ResetPassword';
import { ResellerDashboard } from '@/pages/reseller/Dashboard';
import { NewRequestPage } from '@/pages/reseller/NewRequest';
import { RequestDetailPage } from '@/pages/reseller/RequestDetail';
import { AccountDetailPage } from '@/pages/reseller/AccountDetail';
import { BillingPage } from '@/pages/reseller/Billing';
import { InvoiceDetailPage } from '@/pages/reseller/InvoiceDetail';
import { SalesMarketingPage } from '@/pages/reseller/SalesMarketing';
import { TrainingPage } from '@/pages/reseller/Training';
import { ReferralsPage } from '@/pages/affiliate/Referrals';
import { ReferralDetailPage } from '@/pages/affiliate/ReferralDetail';
import { ReferralToolkitPage } from '@/pages/affiliate/Toolkit';
import { EarningsPage } from '@/pages/affiliate/Earnings';
import { AdminPartnersPage } from '@/pages/admin/Partners';
import { AdminPartnerDetailPage } from '@/pages/admin/PartnerDetail';
import { AdminCommissionsPage } from '@/pages/admin/Commissions';
import { UserRole, PartnerType } from '@/types';
import { AdminDashboard } from '@/pages/admin/Dashboard';
import { AdminRequestDetailPage } from '@/pages/admin/RequestDetail';
import { AdminUsersPage } from '@/pages/admin/Users';
import { AdminBillingPage } from '@/pages/admin/Billing';
import { AdminAssetsPage } from '@/pages/admin/Assets';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({
  children,
  allowedRoles,
  allowedPartnerTypes,
}: {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  /** For partner routes: which partner types may open the page. */
  allowedPartnerTypes?: PartnerType[];
}) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }

  // Partner-type gate: e.g. provisioning and wholesale billing are reseller-only
  if (
    allowedPartnerTypes &&
    user?.role === 'partner' &&
    !allowedPartnerTypes.includes(user.partnerType || 'reseller')
  ) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Home page depends on partner type: resellers manage accounts, affiliates see referrals. */
function PartnerHome() {
  const { user } = useAuth();
  if (user?.partnerType === 'affiliate') {
    return <ReferralsPage />;
  }
  return <ResellerDashboard />;
}

function AppRoutes() {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={user?.role === 'admin' ? '/admin' : '/'} replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          isAuthenticated ? (
            <Navigate to={user?.role === 'admin' ? '/admin' : '/'} replace />
          ) : (
            <ForgotPasswordPage />
          )
        }
      />
      <Route
        path="/reset-password"
        element={
          isAuthenticated ? (
            <Navigate to={user?.role === 'admin' ? '/admin' : '/'} replace />
          ) : (
            <ResetPasswordPage />
          )
        }
      />

      {/* Partner Routes (shared unless gated by allowedPartnerTypes) */}
      <Route
        path="/"
        element={
          <ProtectedRoute allowedRoles={['partner']}>
            <PartnerHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests/new"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['reseller']}>
            <NewRequestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests/:id"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['reseller']}>
            <RequestDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/:sfAccountId"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['reseller']}>
            <AccountDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['reseller']}>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/invoices/:invoiceId"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['reseller']}>
            <InvoiceDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Affiliate-only routes */}
      <Route
        path="/referrals/:id"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['affiliate']}>
            <ReferralDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/toolkit"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['affiliate']}>
            <ReferralToolkitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/earnings"
        element={
          <ProtectedRoute allowedRoles={['partner']} allowedPartnerTypes={['affiliate']}>
            <EarningsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-marketing"
        element={
          <ProtectedRoute allowedRoles={['partner']}>
            <SalesMarketingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute allowedRoles={['partner']}>
            <TrainingPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/requests/:id"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminRequestDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPartnersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners/:id"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPartnerDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/commissions"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCommissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminBillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assets"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminAssetsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
